import { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation, withTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import useBasicGraphCard from '../../../core/hooks/useBasicGraphCard';
import Utils from '../../../core/utilities/Utils';
import ChartMenu from '../../../components/ChartMenu';
import TableViewModal from '../NewDashboard/TableViewModal';
import './ESGAnalyticsLineGraph.scss';

const ESGAnalyticsLineGraph = ({ t, graphData, configRows, showChartMenu = true, showMaximizeInMenu = true, showViewTableInMenu = true }) => {
  const { i18n } = useTranslation();
  const {
    chartData,
    chartOptions,
    setChartData,
    setChartOptions,
    chartRef
  } = useBasicGraphCard(t);

  const [chartReady, setChartReady] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [isMonthView, setIsMonthView] = useState(true);
  const [isMaximized, setMaximized] = useState(false);
  const [showTableView, setShowTableView] = useState(false);
  const [tableViewModalOpen, setTableViewModalOpen] = useState(false);

  useEffect(() => {
    if (graphData && configRows) {
      setupLineChartData(graphData, configRows);
    }
  }, [graphData, configRows, showLabels]);

  const setupLineChartData = (data, configRows) => {
    if (!data || typeof data !== 'object') {
      setChartReady(false);
      return;
    }

    // New response structure: {metrics: [], datapoints: [], equations: []}
    // Each metric/datapoint has: {metricId/dataPointId, metricName/dataPointName, uom, data: [{year, month, value}]}
    // Each equation has: {equation: "expressionWithId", data: [{year, month, value}]}
    const { metrics = [], datapoints = [], equations = [] } = data;

    if (metrics.length === 0 && datapoints.length === 0 && equations.length === 0) {
      setChartReady(false);
      return;
    }

    // Extract only level1 metric, datapoint IDs from configRows to filter which ones to show
    // Store labels from configRow: level1 uses configRow.name
    // Note: Equation enabled l1 items are excluded
    const itemMap = new Map(); // {id: {name, type, code, label, isEquation}}
    
    configRows.forEach(configRow => {
      // Skip equation enabled l1 items - they should not be plotted
      if (configRow.useLevel1Equation && configRow.level1Equation && configRow.level1Equation.expressionWithId) {
        return; // Skip equation enabled l1 items
      }
      
      // Only process normal metric/datapoint level1 items
      if (configRow.level1 && configRow.level1.id) {
        itemMap.set(configRow.level1.id, {
          name: configRow.level1.name || configRow.level1.metricName || configRow.level1.dataPointName || '',
          type: configRow.level1.itemType || 'metric',
          code: configRow.level1.code || configRow.level1.metricCode || configRow.level1.dataPointCode || '',
          label: configRow.name || '', // Use configRow.name for level1 label
          isEquation: false
        });
      }
    });

    if (itemMap.size === 0) {
      setChartReady(false);
      return;
    }

    const labels = [];
    const datasets = [];
    const colors = [
      { borderColor: '#3b82f6', backgroundColor: '#3b82f680' },
      { borderColor: '#10b981', backgroundColor: '#10b98180' },
      { borderColor: '#f59e0b', backgroundColor: '#f59e0b80' },
      { borderColor: '#ef4444', backgroundColor: '#ef444480' },
      { borderColor: '#8b5cf6', backgroundColor: '#8b5cf680' },
      { borderColor: '#06b6d4', backgroundColor: '#06b6d480' },
      { borderColor: '#ec4899', backgroundColor: '#ec489980' },
      { borderColor: '#14b8a6', backgroundColor: '#14b8a680' },
    ];

    // Collect all unique year-month combinations from all metrics, datapoints, and equations
    const allMonthsSet = new Set();
    const monthOrder = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    
    [...metrics, ...datapoints, ...equations].forEach(item => {
      if (item.data && Array.isArray(item.data)) {
        item.data.forEach(d => {
          if (d.year && d.month) {
            allMonthsSet.add(`${d.year}-${d.month}`);
          }
        });
      }
    });

    // Sort all months chronologically
    const allMonths = Array.from(allMonthsSet).sort((a, b) => {
      const [yearA, monthA] = a.split('-');
      const [yearB, monthB] = b.split('-');
      if (yearA !== yearB) {
        return parseInt(yearA) - parseInt(yearB);
      }
      return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
    });

    // Create labels from months
    allMonths.forEach(monthKey => {
      const [year, month] = monthKey.split('-');
      const monthIndex = monthOrder.indexOf(month);
      if (monthIndex >= 0) {
        const date = new Date(parseInt(year), monthIndex, 1);
        const monthName = new Intl.DateTimeFormat(i18n.language, { month: 'short' }).format(date);
        labels.push(`${monthName} ${year}`);
      }
    });

    // Create datasets for each metric/datapoint that exists in configRows
    let colorIndex = 0;
    
    // Process metrics
    metrics.forEach(metric => {
      if (!itemMap.has(metric.metricId)) return;
      
      const itemInfo = itemMap.get(metric.metricId);
      const color = colors[colorIndex % colors.length];
      const data = [];

      // Create a map of year-month to value for quick lookup
      const valueMap = new Map();
      if (metric.data && Array.isArray(metric.data)) {
        metric.data.forEach(d => {
          if (d.year && d.month && d.value != null) {
            valueMap.set(`${d.year}-${d.month}`, d.value);
          }
        });
      }

      // Populate data array for all months
      allMonths.forEach(monthKey => {
        const value = valueMap.get(monthKey) || 0;
        data.push(value);
      });

      // Use label from configRow if available, otherwise fallback to metricName or name
      const displayLabel = itemInfo.label || metric.metricName || itemInfo.name || `Metric ${metric.metricId}`;
      
      const metricUom = (metric.uom && String(metric.uom).trim()) || '';
      datasets.push({
        label: displayLabel,
        uom: metricUom,
        borderColor: color.borderColor,
        backgroundColor: color.backgroundColor,
        data: data,
        tension: 0.4,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
      });

      colorIndex++;
    });

    // Process datapoints
    datapoints.forEach(datapoint => {
      if (!itemMap.has(datapoint.dataPointId)) return;
      
      const itemInfo = itemMap.get(datapoint.dataPointId);
      const color = colors[colorIndex % colors.length];
      const data = [];

      // Create a map of year-month to value for quick lookup
      const valueMap = new Map();
      if (datapoint.data && Array.isArray(datapoint.data)) {
        datapoint.data.forEach(d => {
          if (d.year && d.month && d.value != null) {
            valueMap.set(`${d.year}-${d.month}`, d.value);
          }
        });
      }

      // Populate data array for all months
      allMonths.forEach(monthKey => {
        const value = valueMap.get(monthKey) || 0;
        data.push(value);
      });

      // Use label from configRow if available, otherwise fallback to dataPointName or name
      const displayLabel = itemInfo.label || datapoint.dataPointName || itemInfo.name || `Datapoint ${datapoint.dataPointId}`;
      
      const dpUom = (datapoint.uom && String(datapoint.uom).trim()) || '';
      datasets.push({
        label: displayLabel,
        uom: dpUom,
        borderColor: color.borderColor,
        backgroundColor: color.backgroundColor,
        data: data,
        tension: 0.4,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
      });

      colorIndex++;
    });

    // Skip processing equations for l1 items (equation enabled l1 items are filtered out)

    const yAxisUomTitle = (() => {
      const uoms = new Set();
      metrics.forEach((metric) => {
        if (!itemMap.has(metric.metricId)) return;
        const u = metric.uom && String(metric.uom).trim();
        if (u) uoms.add(u);
      });
      datapoints.forEach((dp) => {
        if (!itemMap.has(dp.dataPointId)) return;
        const u = dp.uom && String(dp.uom).trim();
        if (u) uoms.add(u);
      });
      if (uoms.size === 0) return '';
      return Array.from(uoms).sort().join(' / ');
    })();

    const cOption = {
      layout: {
        padding: {
          top: 20,
          bottom: 5,
          left: 5,
          right: 5,
        },
      },
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          align: 'start',
          labels: {
            font: {
              size: 12
            },
            usePointStyle: true,
            padding: 15
          },
          display: true
        },
        tooltip: {
          callbacks: {
            label: (tooltipItem) => {
              const value = tooltipItem?.dataset?.data[tooltipItem.dataIndex] ?? 0;
              const uom = tooltipItem.dataset.uom;
              const num = Utils.formattedNumber(value);
              const suffix = uom ? ` ${uom}` : '';
              return ` ${tooltipItem.dataset.label}: ${num}${suffix}`;
            }
          }
        },
        datalabels: {
          display: showLabels,
          color: '#374151',
          font: { size: 11, weight: '600' },
          anchor: 'center',
          align: 'top',
          formatter: (value) => (value === 0 ? '' : Utils.formattedNumber(value)),
        }
      },
      scales: {
        x: {
          ticks: {
            font: {
              size: 11
            },
            maxRotation: 45,
            minRotation: 45
          },
          grid: {
            display: true,
            color: 'rgba(200, 200, 200, 0.2)'
          }
        },
        y: {
          title: {
            display: Boolean(yAxisUomTitle),
            text: yAxisUomTitle,
            font: { size: 11 },
            color: '#64748b',
          },
          ticks: {
            font: {
              size: 11
            },
            callback: function(value) {
              return Utils.formattedNumber(value);
            }
          },
          grid: {
            display: true,
            color: 'rgba(200, 200, 200, 0.2)'
          }
        },
      },
    };

    setChartOptions(cOption);
    setChartData({
      labels,
      datasets: datasets,
    });
    setChartReady(true);
  };

  // Chart menu handlers
  const handleGetInfo = () => {
    // Info modal can be implemented later
  };

  const handleToggleLabels = () => {
    setShowLabels(!showLabels);
  };

  const handleToggleView = () => {
    setIsMonthView(!isMonthView);
  };

  const handleMaximize = () => {
    setMaximized(!isMaximized);
  };

  const handleViewTable = () => {
    setTableViewModalOpen(true);
  };

  const handleCloseTableViewModal = () => {
    setTableViewModalOpen(false);
  };

  const handleDownload = () => {
    if (chartRef?.current) {
      const canvas = chartRef.current.canvas;
      const ctx = canvas.getContext('2d');
      ctx.save();
      const downloadCanvas = document.createElement('canvas');
      const dCtx = downloadCanvas.getContext('2d');
      downloadCanvas.width = canvas.width;
      downloadCanvas.height = canvas.height;
      dCtx.fillStyle = '#ffffff';
      dCtx.fillRect(0, 0, downloadCanvas.width, downloadCanvas.height);
      dCtx.drawImage(canvas, 0, 0);
      const link = document.createElement('a');
      link.href = downloadCanvas.toDataURL('image/jpeg', 0.9);
      link.download = `${t("ESGAnalytics.comparison.label") || 'comparison'}.jpg`;
      link.click();
      ctx.restore();
    }
  };

  if (!graphData || !chartReady) {
    return null;
  }

  return (
    <>
      <Card className="esg-analytics-line-graph-card mb-3 pt-0">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{t("ESGAnalytics.comparison.label")}</h5>
          {showChartMenu && (
            <ChartMenu
              chartId="esg-analytics-comparison-chart"
              onToggleLabels={handleToggleLabels}
              onMaximize={handleMaximize}
              onViewTable={handleViewTable}
              onDownload={handleDownload}
              showLabels={showLabels}
              isMaximized={isMaximized}
              showTableView={tableViewModalOpen}
              chartType="LINE"
              showGetInfo={false}
              showToggleLabels={true}
              showToggleView={false}
              showMaximize={showMaximizeInMenu}
              showDataSources={false}
              showViewTable={showViewTableInMenu}
              showDownload={true}
              showDeactivate={false}
            />
          )}
        </Card.Header>
        <Card.Body>
          <div className="line-graph-container">
            <Line data={chartData} options={chartOptions} ref={chartRef} plugins={[ChartDataLabels]} />
          </div>
        </Card.Body>
      </Card>

      {/* Table View Modal */}
      {showChartMenu && (
        <TableViewModal
          open={tableViewModalOpen}
          onClose={handleCloseTableViewModal}
          chartData={chartData}
          chartConfig={{ type: 'LINE' }}
          title={t("ESGAnalytics.comparison.label") || "By Source"}
        />
      )}
    </>
  );
};

export default withTranslation()(ESGAnalyticsLineGraph);

